import React from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { MAX_SELECTED_SITES } from '../../../../../common/constants/filters/site';

import { i18n } from './messages';
import { SiteAvatar } from './site-avatar';

export const SELECTED_GROUP = 'selected';
export const UNSELECTED_GROUP = 'unselected';

const styles = cssMap({
	optionText: {
		font: token('font.body'),
		color: token('color.text'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

// De-emphasised hint (smaller, italic) used for the "(Select up to N)" heading suffix.
const renderHint = (chunks: React.ReactNode[]): React.ReactNode => (
	<Text as="em" weight="regular" size="small" color="inherit">
		{chunks}
	</Text>
);

// Italic "(current)" suffix that keeps the same font size as the surrounding option label.
const renderCurrentSuffix = (chunks: React.ReactNode[]): React.ReactNode => (
	<Text as="em" weight="regular" color="inherit">
		{chunks}
	</Text>
);

export const sortAlphabetically = (a: { label?: string }, b: { label?: string }): number =>
	(a.label ?? '').localeCompare(b.label ?? '');

type SiteOption = {
	label?: React.ReactNode;
	isSelected?: boolean;
};

// The current/primary site is display-only: injected as an always-selected, locked row, never
// stored in the filter (so it's never serialized to `?sites=`).
export const buildCurrentSiteOption = (
	primarySiteId: string | undefined,
	primarySiteName: string | undefined,
): (SelectFilterOption & { isDisabled: boolean }) | undefined =>
	primarySiteId
		? {
				value: primarySiteId,
				trackingKey: primarySiteId,
				queryParamValue: primarySiteId,
				label: primarySiteName || primarySiteId,
				isSelected: true,
				isSuggested: false,
				isDefault: false,
				isDisabled: true,
			}
		: undefined;

// Heading for the selected group: "(Maximum selected)" at the cap, "(Select up to N)" when the cap
// is reachable, otherwise a plain "Selected".
export const SelectedHeading = ({
	atCap,
	capReachable,
}: {
	atCap: boolean;
	capReachable: boolean;
}): React.JSX.Element => {
	if (atCap) {
		return <FormattedMessage {...i18n.selectedHeadingMaxReached} values={{ hint: renderHint }} />;
	}

	if (capReachable) {
		return (
			<FormattedMessage
				{...i18n.selectedHeadingMax}
				values={{ max: MAX_SELECTED_SITES, hint: renderHint }}
			/>
		);
	}

	return <FormattedMessage {...i18n.selectedHeadingPlain} />;
};

// A single site row (avatar + name). The current site's row appends an italicised "(current)".
export const SiteOptionLabel = ({
	option,
	isCurrent,
}: {
	option: SiteOption;
	isCurrent: boolean;
}): React.JSX.Element => {
	const name = typeof option.label === 'string' ? option.label : '';
	return (
		<Inline alignBlock="center" space="space.100">
			<SiteAvatar displayName={name} isActive={Boolean(option.isSelected)} />
			<Box xcss={styles.optionText}>
				{isCurrent ? (
					<FormattedMessage
						{...i18n.currentSiteLabel}
						values={{ name, hint: renderCurrentSuffix }}
					/>
				) : (
					name
				)}
			</Box>
		</Inline>
	);
};
